import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import zh from '../i18n/zh.json';
import en from '../i18n/en.json';
import es from '../i18n/es.json';
import { loadAuthUser } from '../services/auth';
import { getUserStorageKey } from './storage';

// 合并Tailwind类名的工具函数
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// 创建页面URL的工具函数
export function createPageUrl(pageName: string): string {
  const routes: Record<string, string> = {
    'Home': '/',
    'WhisperTalk': '/whisper',
    'ContentLibrary': '/library',
    'Profile': '/profile',
    'AudioPlayer': '/player',
    'Alarm': '/alarm',
    'FocusMode': '/focus',
    'GuidedMeditation': '/meditation',
    'SleepFeedback': '/feedback',
    'Subscription': '/purchase',
    'Support': '/support',
    'Privacy': '/privacy',
    'EditProfile': '/edit-profile',
    'Journals': '/journals',
    'CheckIn': '/check-in',
    'CheckInEvent': '/check-in/event',
    'CheckInEventCustomize': '/check-in/event/customize',
    'CheckInEventCustomizeIcon': '/check-in/event/customize/icon',
    'CheckInSummary': '/check-in/summary'
  };
  
  return routes[pageName] || '/';
}

// 格式化时间
export function formatTime(date: Date): string {
  return date.toLocaleTimeString('zh-CN', { 
    hour: '2-digit', 
    minute: '2-digit' 
  });
}

// 格式化持续时间
export function formatDuration(minutes: number): string {
  if (minutes < 60) {
    return `${minutes}分钟`;
  }
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return remainingMinutes > 0 ? `${hours}小时${remainingMinutes}分钟` : `${hours}小时`;
}

// 生成唯一ID
export function generateId(): string {
  return `id_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

// 防抖函数
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

// 节流函数
export function throttle<T extends (...args: any[]) => any>(
  func: T,
  limit: number
): (...args: Parameters<T>) => void {
  let inThrottle: boolean;
  return (...args: Parameters<T>) => {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
}

// ==================== 语言与翻译 ====================
const DEFAULT_LANGUAGE = 'en-US';
const translations: Record<string, Record<string, string>> = {
  'zh-CN': zh,
  'en-US': en,
  'es-ES': es,
};

// 读取当前语言（优先使用已保存的设置）
const getCurrentLanguage = (): string => {
  try {
    const user = loadAuthUser();
    const savedSettings = localStorage.getItem(getUserStorageKey('app-settings', user?.id));
    if (savedSettings) {
      const parsed = JSON.parse(savedSettings);
      if (parsed?.language && translations[parsed.language]) {
        return parsed.language;
      }
    }
  } catch (error) {
    console.error('Failed to read app settings for language:', error);
  }

  if (typeof navigator !== 'undefined') {
    const deviceLang = navigator.language || (navigator as any).userLanguage || DEFAULT_LANGUAGE;
    if (deviceLang.startsWith('zh')) return 'zh-CN';
    if (deviceLang.startsWith('es')) return 'es-ES';
    if (deviceLang.startsWith('en')) return 'en-US';
  }

  return DEFAULT_LANGUAGE;
};

const getLocalizedDefaultUserName = (): string => {
  const lang = getCurrentLanguage();
  return translations[lang]?.['home.user'] || translations[DEFAULT_LANGUAGE]?.['home.user'] || 'User';
};

// ==================== Onboarding 数据管理 ====================
export type OnboardingData = {
  name?: string;
  goal?: 'fall_asleep' | 'relax' | 'reduce_anxiety' | 'reflect';
  voice?: string;
  intents?: string[];
};

/**
 * 从 localStorage 读取用户 onboarding 数据
 */
export function getOnboardingData(): OnboardingData | null {
  try {
    const user = loadAuthUser();
    const data = localStorage.getItem(getUserStorageKey('onboarding-data', user?.id));
    if (!data) return null;
    return JSON.parse(data) as OnboardingData;
  } catch (error) {
    console.error('Failed to load onboarding data:', error);
    return null;
  }
}

/**
 * 保存 onboarding 数据到 localStorage
 */
export function saveOnboardingData(data: OnboardingData): void {
  try {
    const user = loadAuthUser();
    localStorage.setItem(getUserStorageKey('onboarding-data', user?.id), JSON.stringify(data));
  } catch (error) {
    console.error('Failed to save onboarding data:', error);
  }
}

/**
 * 获取用户名称（优先从 onboarding 数据，其次从 auth user）
 */
export function getUserDisplayName(fallback?: string): string {
  const onboarding = getOnboardingData();
  if (onboarding?.name?.trim()) {
    return onboarding.name.trim();
  }
  if (fallback?.trim()) {
    return fallback.trim();
  }
  return getLocalizedDefaultUserName();
}




export function isValidUUID(uuid: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(uuid);
}
